import { useMutation } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { type ContactSubmission } from "../ContactForm/validation";
import { ContactsTable, createSupplyChainPartnerMutation, supplyChainPartnersQuery } from "../ContactsTable";
import { PageHeader } from "@/components/PageHeader";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ContactForm } from "@/contacts/ContactForm";
import { type ContactFilters } from "@/contacts/schemas/contactFilterSchema";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { nullable } from "@/lib/utils/common";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { transformForApi } from "@/lib/utils/transforms";

const routeApi = getRouteApi("/(authed)/_layout/contacts");

export function Contacts() {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<ContactFilters>(routeApi.useSearch());

  const handleFilterUpdate = (newFilters: ContactFilters) => {
    setFilters(newFilters);
  };
  const [createSupplyChainPartner] = useMutation(createSupplyChainPartnerMutation, {
    refetchQueries: [supplyChainPartnersQuery],
  });
  const { toast } = useToast();
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const onSubmit = async (values: ContactSubmission) => {
    try {
      if (!activeBusinessUnitId) {
        toast({
          title: "Error",
          description: "Missing active business unit.",
          variant: "destructive",
        });

        return;
      }

      const result = await createSupplyChainPartner({
        variables: {
          input: {
            businessName: transformForApi.text(values.businessName),
            role: values.role,
            type: values.type,
            addressLine1: transformForApi.text(values.addressLine1),
            addressLine2: nullable(transformForApi.text(values.addressLine2)),
            state: values.state,
            city: transformForApi.text(values.city),
            postcode: transformForApi.text(values.postCode),
            managedBy: nullable(values.businessAccountManagerId),
            country: values.countryId,
            owner: activeBusinessUnitId,
            contactEmail: nullable(transformForApi.email(values.contactEmail)),
            contactPhone: values.contactPhone,
            contactFirstName: nullable(transformForApi.text(values.contactFirstName)),
            contactLastName: nullable(transformForApi.text(values.contactLastName)),
            accountNumber: nullable(transformForApi.identifier(values.accountNumber)),
            monthlyTarget: !!values.monthlyTarget ? Number(values.monthlyTarget) : null,
            note: nullable(values.note),
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error creating ${values.businessName}`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsOpen(false);

      toast({
        title: "Success",
        description: `${values.businessName} successfully created.`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: `Error creating ${values.businessName}`,
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      <PageHeader>
        <PageHeader.Content>
          <PageHeader.Title>Contacts</PageHeader.Title>
        </PageHeader.Content>
        <PageHeader.Actions>
          <Button
            variant={"confirm"}
            onClick={() => {
              setIsOpen(true);
            }}
          >
            Create Contact
          </Button>
        </PageHeader.Actions>
      </PageHeader>
      <ContactsTable filters={filters} onUpdateFilters={handleFilterUpdate} />
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Create Contact</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Create Contact Form</AlertDialogDescription>
          </AlertDialogHeader>
          <ContactForm onSubmit={onSubmit} onCancel={onCancel} />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
